import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useState, useEffect } from 'react';

// LinearProgressWithLabel component matching the upload progress bar
const LinearProgressWithLabel = ({ value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
            <LinearProgress variant={value !== null ? "determinate" : "indeterminate"} value={value || 0} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
            <Typography variant="body2" color="white">{value !== null ? `${Math.round(value)}%` : ''}</Typography>
        </Box>
    </Box>
);

const Loading = ({ value = null, title = "Loading Data..." }) => {
    const [startTime] = useState(Date.now());
    const [estimatedTime, setEstimatedTime] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            const elapsed = Math.floor((now - startTime) / 1000);
            setElapsedTime(elapsed);

            // Calculate estimated time remaining if we have progress
            if (value && value > 0 && value < 100 && elapsed > 2) { // Wait at least 2 seconds for stable calculation
                // Calculate rate: percentage per second
                const rate = value / elapsed;
                // Calculate remaining percentage
                const remainingPercentage = 100 - value;
                // Calculate estimated remaining time
                const remaining = Math.floor(remainingPercentage / rate);
                setEstimatedTime(Math.max(0, remaining));

            } else if (value >= 100) {
                setEstimatedTime(0);
            } else {
                setEstimatedTime(null);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [startTime, value]); // Added value to dependencies

    const formatTime = (seconds) => {
        if (seconds < 60) {
            return `${seconds}s`;
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes}m ${remainingSeconds}s`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return `${hours}h ${minutes}m`;
        }
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw',
                backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent background
                zIndex: 9999, // High z-index to appear above other elements
                backdropFilter: 'blur(5px)' // Optional: adds a blur effect to background
            }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '400px',
                maxWidth: '80vw'
            }}>
                <Typography variant="h5" color="white" sx={{ mb: 3, textAlign: 'center' }}>
                    {title}
                </Typography>

                <Box sx={{ width: '100%', mb: 2 }}>
                    <LinearProgressWithLabel value={value} />
                </Box>

                {/* Time information */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    px: 1
                }}>
                    <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                        Elapsed: {formatTime(elapsedTime)}
                    </Typography>
                    {estimatedTime !== null && estimatedTime > 0 && (
                        <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                            Est. remaining: {formatTime(estimatedTime)}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default Loading;